/**
 * WrapUpView — the encounter Wrap-Up step container. It decides WHICH sections exist (role and
 * feature gates stay here, in the page's slices) and computes the progress value; the Signal DS
 * `WrapUpLayout` template owns everything visual — the 920 column, the 24 rhythm, the header run,
 * the progress rail and the 70px invoice tail (see its own docblock for the Flutter citations).
 *
 * Slots, in Flutter's `selectedItemsView` order (wrap_up_billing_screen.dart:661-689):
 *   header       — EncounterWrapUpActionsHeader (the page-fed `status` + `signLock` slices; the
 *                  Send-Orders and Sign handlers stay 1:1)
 *   problems     — M4b "Problem list" closing step (ratified product work, not in Flutter)
 *   instructions — DS `WrapUpInstructionsPanel` (WrapUpModel.instructions)
 *   followUp     — WrapUpFollowUp (PROD-1405), doctor-only (the page omits it for nurses)
 *   invoice      — WrapUpInvoiceSection (`Show_Billing_Summary` && !isNurse, applied by the page),
 *                  followed by WrapUpPendingOrders (PROD-2594) under the same gate — a local
 *                  sibling, because the DS `WrapUpInvoicePanel` organism has no slot for it
 *
 * `summary` (Post-Visit Summary, PROD-1285) stays ACCEPTED and NOT RENDERED — Flutter's wrap-up has
 * no inline PVS block; it opens as a popup from the Send-orders preview action
 * (wrap_up_billing_screen.dart:417-425).
 */
import {
  cn,
  ProgressBar,
  WrapUpInstructionsPanel,
  WrapUpLayout,
  type AuthorRole,
  type WrapUpInstructionsPanelProps,
} from '@scripsteam/scrips-signal-ds';
import type { StructuredNotePhrases } from './structured-note/structured-note-section';
import {
  EncounterWrapUpActionsHeader,
  type WrapUpStatusProps,
  type WrapUpSignLockProps,
  type WrapUpEPrescriptionProps,
  type WrapUpNoteActionsProps,
} from './wrap-up-actions-header';
import { WrapUpFollowUp, type WrapUpFollowUpProps } from './wrap-up-follow-up';
import { WrapUpFollowUpLine, WrapUpInstructionsLine } from './wrap-up-note-lines';
import { WrapUpProblems, type WrapUpProblemsProps } from './wrap-up-problems';
import { WrapUpInvoiceSection, type WrapUpInvoiceSectionProps } from './wrap-up-invoice-section';
import { WrapUpPendingOrders, type WrapUpPendingOrdersProps } from './wrap-up-pending-orders';
import type { WrapUpSummaryProps } from './wrap-up-summary';
import { ReferralHandoff, type ReferralHandoffView } from '@scripsteam/signal';
import { SignalScope } from './nurse-med-rec/nurse-med-rec-block';
import { useState, type ReactNode } from 'react';
import { useDesignV2, V2_META } from './lib/design-v2';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** Section 6 look (`?x=wrap-up-v3`, ON by default on the dev hosts): read once per mount, like the frame. */
function useWrapUpV3(): boolean {
  const [on] = useState(() => isEncounterFlagOn('wrapUpV3'));
  return on;
}

/**
 * Design sheet (switch ON only; SYSTEM-RULES "Cards": white, `--radius-card`, 1px
 * `--color-border-card`, resting `--shadow-2`; "a card is flat only with a recorded exception").
 * The same card the pre-visit Active Medications block uses ON (encounter-workflow.tsx), so the
 * step reads as one card language. Measured on dev before (1 Oct, Samer: "it's all flat"): one
 * white sheet with two hairline boxes of different radii and two bare sections. Three cards, one
 * per task group (second opinion, Codex): Problem list · Patient instructions + Follow-up (one
 * patient-facing plan; Follow-up alone is 56px) · Invoice (diagnosis, services, totals, pending
 * orders). The header and the tail stay bare.
 */
const WRAP_UP_CARD_V2 =
  'bg-surface-card border border-solid border-border-card rounded-card p-4 shadow-2 flex flex-col gap-4 w-full';

export interface WrapUpViewProps {
  /** TS-3 · frame v2: inside the note's "Wrap-up" block — the header draws no title of its own. */
  untitled?: boolean;
  /** Frame v2 only: drawn inside the note's Wrap-up block. The action bar then has no Sign quill (the
   *  note header signs, FV2-05) and no "coming soon" control (FV2-06). See `EncounterWrapUpActionsHeader`. */
  inNote?: boolean;
  /** PROD-3148 · frame v2 (in the note): the note's phrase library for Patient instructions, and whose note it is. */
  phrases?: StructuredNotePhrases;
  author?: AuthorRole;
  /** Send-Orders + sign progress — feeds the header's SEND ORDERS / SIGN NOTE clusters. */
  status?: WrapUpStatusProps;
  /** Sign slice — feeds the SIGN NOTE cluster's pen action (PROD-1282 gates, 1:1). */
  signLock?: WrapUpSignLockProps;
  /** ePrescription cluster slice (Flutter `showRx` + isRxSynced + send). */
  ePrescription?: WrapUpEPrescriptionProps;
  /**
   * M4b — the "Problem list" closing step (four-moments wave, spec §10.5, RATIFIED). NEVER gates
   * signing (Samer law) — this slice carries no sign wiring.
   */
  problems?: WrapUpProblemsProps;
  /** PATIENT INSTRUCTIONS slice (WrapUpModel.instructions). */
  instructions?: WrapUpInstructionsPanelProps;
  /** Follow-up scheduler (PROD-1405) — doctor-only (the page omits it for nurses). */
  followUp?: WrapUpFollowUpProps;
  /** The INVOICE block — omitted by the page when `Show_Billing_Summary` fails or for a nurse. */
  invoiceSection?: WrapUpInvoiceSectionProps;
  /**
   * Pending orders from Assessment & Plan (PROD-2594), rendered under the invoice panel. Only
   * rendered together with `invoiceSection` — it shares that gate and never shows on its own.
   */
  pendingOrders?: WrapUpPendingOrdersProps;
  /** ACCEPTED, NOT RENDERED — see the file header. */
  summary?: WrapUpSummaryProps;
  /** PROD-2743 frame v2 only: View note · Clinical edits · Discard in the action bar. */
  noteActions?: WrapUpNoteActionsProps;
  /** The long page: the note actions and Send orders are in the page's Wrap-up foot, not in the action bar (`footActions`). */
  footActions?: boolean;
  /** The foot really draws Send orders: only then does the action bar leave its own out (`footSendOrders`). */
  footSendOrders?: boolean;
  /** A quiet line atop the instructions + follow-up — "Not part of an amendment" while a provider-signed note
   *  is amended (rule 38; the wrap-up record has no amendment path). Absent → none, byte-identical. */
  patientNotice?: string;
  /** `referralHandoff` (physician only): the referrals this encounter sends to the care team, each as Antonin's hand-off card,
   *  sender side (catalogue 41). Absent or empty: nothing drawn, byte-identical. */
  referrals?: (ReferralHandoffView & { id: string })[];
}

/**
 * Flutter's wrap-up progress rail (wrap_up_billing_screen.dart:133-145, 214-222): 50 once orders
 * are sent, 100 once signed — a nurse jumps straight to 100 on sign, since the nurse flow has no
 * send-orders half. 0 hides the rail (the layout draws nothing at 0).
 */
function wrapUpProgress(status?: WrapUpStatusProps, signLock?: WrapUpSignLockProps): number {
  const signed = status?.signed ?? signLock?.state === 'locked';
  const half = status?.orderSent ?? false;
  const nurse = signLock?.isNurse ?? false;
  return signed && (half || nurse) ? 100 : half ? 50 : 0;
}

export function WrapUpView({
  untitled,
  inNote,
  status,
  signLock,
  ePrescription,
  problems,
  instructions,
  followUp,
  invoiceSection,
  pendingOrders,
  noteActions,
  footActions,
  footSendOrders,
  patientNotice,
  phrases,
  author,
  referrals,
}: WrapUpViewProps) {
  // The header is passed only when the step has SOMETHING to show — an `ePrescription` slice alone
  // is not enough, exactly as before — so the layout's own empty placeholder still renders for a
  // step the page has not fed.
  const fed = Boolean(status || signLock || problems || instructions || followUp || invoiceSection);
  const designV2 = useDesignV2();
  const v3 = useWrapUpV3();
  const progress = inNote ? 0 : wrapUpProgress(status, signLock);
  // PROD-2947 (6 Oct contract G2): inside the note's Wrap-up block nothing is a card within the block — the groups
  // keep their slots, without the card frame. Outside the note (the step canvas) the cards are as before.
  const card = (slot: string, node: ReactNode) =>
    inNote ? (
      <div data-slot={slot} className="flex flex-col gap-4 w-full">
        {node}
      </div>
    ) : designV2 ? (
      <div data-slot={slot} className={WRAP_UP_CARD_V2}>
        {node}
      </div>
    ) : (
      node
    );
  // Frame v2, inside the note (signal-ds 2.20, FV2-29): the filled row's actions are named for what they act
  // on, and a Remove offers Undo. OFF: no key.
  // PROD-2947 (6 Oct contract K6 / K1, ENC-L11): inside the note the two patient-facing items are note lines — the
  // instructions as an inline text line (Add / Edit, never a "coming soon" action), the follow-up unit as a closed pick
  // list instead of a radio group (wrap-up-note-lines.tsx). Same values, same writes.
  const instructionsNode = instructions ? (
    inNote ? (
      <WrapUpInstructionsLine
        value={instructions.value}
        onChange={instructions.onChange}
        saving={instructions.saving}
        disabled={instructions.disabled}
        phrases={phrases}
        author={author}
      />
    ) : (
      <WrapUpInstructionsPanel {...instructions} />
    )
  ) : undefined;
  const followUpNode = followUp ? inNote ? <WrapUpFollowUpLine {...followUp} /> : <WrapUpFollowUp {...followUp} /> : undefined;
  // Rule 38 while amending (ON and OFF): one quiet line atop the instructions + follow-up, DS secondary text.
  const noticeNode = patientNotice ? (
    <p className={cn(V2_META, 'm-0')} data-testid="wrap-up-patient-notice">
      {patientNotice}
    </p>
  ) : null;
  // Section 6 (`wrapUpV3`): the catalog draws the progress track from 0 ("nothing done yet"), outside the note only. The DS
  // layout draws no bar at 0, so the empty track rides at the top of the header run (showcase-wrap-up.css gives it the 24 gap).
  const idleTrack =
    v3 && !inNote && fed && progress === 0 ? (
      <ProgressBar value={0} aria-label="Wrap-up progress" className="wrap-up-idle-track" />
    ) : null;
  // Every line of a referral is Antonin's (ReferralHandoff, side "sent"), inside the scoped tokens: nothing drawn here.
  const referralsNode =
    referrals && referrals.length > 0 ? (
      <SignalScope slot="referral-handoff-sent">
        <div data-testid="referral-handoff-sent" className="flex flex-col gap-4">
          {referrals.map(({ id, ...view }) => (
            <ReferralHandoff key={id} view={view} side="sent" />
          ))}
        </div>
      </SignalScope>
    ) : null;
  const layout = (
    <WrapUpLayout
      // Inside the note the progression is the closing card's status line (note-status-line.tsx): no bar here.
      progress={progress}
      header={
        fed ? (
          <>
          {idleTrack}
          <EncounterWrapUpActionsHeader
            status={status}
            signLock={signLock}
            ePrescription={ePrescription}
            {...(noteActions ? { noteActions } : {})}
            {...(untitled ? { untitled } : {})}
            {...(inNote ? { inNote } : {})}
            {...(footActions ? { footActions } : {})}
            {...(footSendOrders ? { footSendOrders } : {})}
          />
          </>
        ) : undefined
      }
      problems={
        referralsNode
          ? (
              <>
                {problems ? card('wrap-up-card-problems', <WrapUpProblems {...problems} />) : null}
                {card('wrap-up-card-referrals', referralsNode)}
              </>
            )
          : problems
            ? card('wrap-up-card-problems', <WrapUpProblems {...problems} />)
            : undefined
      }
      /* ON: Instructions and Follow-up share one card (the layout's followUp slot is then empty).
         OFF: two slots, exactly as before. */
      instructions={
        designV2
          ? instructionsNode || followUpNode
            ? card(
                'wrap-up-card-patient',
                <>
                  {noticeNode}
                  {instructionsNode}
                  {followUpNode}
                </>,
              )
            : undefined
          : noticeNode && instructionsNode
            ? (
                <>
                  {noticeNode}
                  {instructionsNode}
                </>
              )
            : instructionsNode
      }
      followUp={designV2 ? undefined : followUpNode}
      invoice={
        invoiceSection
          ? card(
              'wrap-up-card-invoice',
              <>
                <WrapUpInvoiceSection {...invoiceSection} {...(inNote ? { embedded: true } : {})} />
                {pendingOrders && <WrapUpPendingOrders {...pendingOrders} />}
              </>,
            )
          : undefined
      }
    />
  );
  // `contents`: the scope adds no box, so the layout sits in its parent exactly as before.
  return v3 ? (
    <div data-wrap-up-v3={inNote ? 'note' : 'page'} className="contents">
      {layout}
    </div>
  ) : (
    layout
  );
}
